const ANON_ID_KEY = 'rkube_anon_id';
const ANON_FP_ID_KEY = 'rkube_anon_fp_id';
const SESSION_ID_KEY = 'rkube_session_id';
const SESSION_START_KEY = 'rkube_session_started_at';
const ENTRY_PAGE_KEY = 'rkube_entry_page';
const LAST_PAGE_KEY = 'rkube_last_page';
const NEXT_PAGE_KEY = 'rkube_next_page_known';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function randId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function safeGetStorage(key, useSession = false) {
  try {
    const storage = useSession ? window.sessionStorage : window.localStorage;
    return storage.getItem(key);
  } catch (_e) {
    return null;
  }
}

function safeSetStorage(key, value, useSession = false) {
  try {
    const storage = useSession ? window.sessionStorage : window.localStorage;
    storage.setItem(key, value);
  } catch (_e) {
    // best-effort only
  }
}

function getCookie(name) {
  try {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const c of cookies) {
      const idx = c.indexOf('=');
      const k = idx > -1 ? c.slice(0, idx) : c;
      const v = idx > -1 ? c.slice(idx + 1) : '';
      if (k === name) return decodeURIComponent(v);
    }
  } catch (_e) {
    // best-effort only
  }
  return '';
}

function setCookie(name, value, days = 365) {
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=.rkubeservices.com; SameSite=Lax`;
  } catch (_e) {
    // best-effort only
  }
}

function simpleHash(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash +=
      (hash << 1) +
      (hash << 4) +
      (hash << 7) +
      (hash << 8) +
      (hash << 24);
  }
  return `fp_${(hash >>> 0).toString(16)}`;
}

async function sha256Hex(input) {
  if (!window.crypto || !window.crypto.subtle) {
    return simpleHash(input);
  }
  const msgUint8 = new TextEncoder().encode(input);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return `fp_${hashHex.slice(0, 24)}`;
}

function getCanvasFingerprintPart() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('rkube-fp', 2, 2);
    return canvas.toDataURL().slice(0, 120);
  } catch (_e) {
    return '';
  }
}

function getWebGLPart() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
    return `${vendor}|${renderer}`;
  } catch (_e) {
    return '';
  }
}

async function createFingerprint() {
  const parts = [
    navigator.userAgent || '',
    navigator.language || '',
    navigator.platform || '',
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    `${window.screen?.colorDepth || 0}`,
    `${navigator.hardwareConcurrency || 0}`,
    `${navigator.deviceMemory || 0}`,
    `${navigator.maxTouchPoints || 0}`,
    getCanvasFingerprintPart(),
    getWebGLPart(),
  ];
  return sha256Hex(parts.join('||'));
}

function classifyUserAgent(ua) {
  const v = (ua || '').toLowerCase();
  if (v.includes('iphone') || v.includes('android') || v.includes('mobile')) return 'mobile';
  if (v.includes('ipad') || v.includes('tablet')) return 'tablet';
  return 'desktop';
}

function getDeviceType() {
  const w = window.innerWidth || 0;
  if (w <= 767) return 'mobile';
  if (w <= 1024) return 'tablet';
  return 'desktop';
}

function getOrCreateAnonId() {
  const cookieName = 'rkube_anon_id';
  let anonId = safeGetStorage(ANON_ID_KEY) || getCookie(cookieName);
  if (!anonId) {
    anonId = randId('anon');
  }
  safeSetStorage(ANON_ID_KEY, anonId);
  setCookie(cookieName, anonId, 365);
  return anonId;
}

async function getOrCreateAnonFingerprintId() {
  let fpId = safeGetStorage(ANON_FP_ID_KEY);
  if (!fpId) {
    fpId = await createFingerprint();
    safeSetStorage(ANON_FP_ID_KEY, fpId);
  }
  return fpId;
}

function getOrCreateSessionId() {
  let sessionId = safeGetStorage(SESSION_ID_KEY, true);
  if (!sessionId) {
    sessionId = randId('sess');
    safeSetStorage(SESSION_ID_KEY, sessionId, true);
    safeSetStorage(SESSION_START_KEY, String(Date.now()), true);
  }
  return sessionId;
}

function getSessionElapsedSeconds() {
  const startedAt = Number(safeGetStorage(SESSION_START_KEY, true) || Date.now());
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}

function getOrSetEntryPage(currentPage) {
  let entry = safeGetStorage(ENTRY_PAGE_KEY, true);
  if (!entry) {
    entry = currentPage;
    safeSetStorage(ENTRY_PAGE_KEY, entry, true);
  }
  return entry;
}

function getLastPage() {
  return safeGetStorage(LAST_PAGE_KEY, true) || '';
}

function setLastPage(page) {
  safeSetStorage(LAST_PAGE_KEY, page, true);
}

export function setNextKnownPage(nextPage) {
  if (nextPage) {
    safeSetStorage(NEXT_PAGE_KEY, nextPage, true);
  }
}

function consumeNextKnownPage() {
  const next = safeGetStorage(NEXT_PAGE_KEY, true) || '';
  if (next) {
    safeSetStorage(NEXT_PAGE_KEY, '', true);
  }
  return next;
}

export async function getTrackingIdentity() {
  const anon_id = getOrCreateAnonId();
  const anon_fp_id = await getOrCreateAnonFingerprintId();
  const session_id = getOrCreateSessionId();
  return { anon_id, anon_fp_id, session_id };
}

function sendLocalTelemetry(body) {
  const url = API_BASE ? `${API_BASE}/telemetry/events` : '/telemetry/events';
  const payload = JSON.stringify(body);

  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    } catch (_e) {
      // fallback to fetch
    }
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    // best-effort only
  });
}

export async function trackTelemetryEvent(eventName, payload = {}, options = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  const identity = await getTrackingIdentity();
  const currentPage = payload.current_page || window.location.pathname;
  const pageWithQuery = `${window.location.pathname}${window.location.search || ''}`;
  const entryPage = getOrSetEntryPage(pageWithQuery);
  const referrerPage = payload.referrer_page || getLastPage() || document.referrer || '';
  const nextPageKnown = payload.next_page_known || consumeNextKnownPage();

  const base = {
    event_name: eventName,
    event_ts: new Date().toISOString(),
    anon_id: identity.anon_id,
    anon_fp_id: identity.anon_fp_id,
    session_id: identity.session_id,
    page_path: window.location.pathname,
    entry_page: entryPage,
    current_page: currentPage,
    referrer_page: referrerPage,
    next_page_known: nextPageKnown || undefined,
    user_agent_class: classifyUserAgent(navigator.userAgent || ''),
    device_type: getDeviceType(),
    elapsed_seconds: getSessionElapsedSeconds(),
  };

  const merged = { ...base, ...payload };

  const inferredAttribution = payload.attribution
    ? payload.attribution
    : {
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        utm_term: payload.utm_term,
        utm_content: payload.utm_content,
        gclid: payload.gclid,
        hsa_acc: payload.hsa_acc,
        hsa_cam: payload.hsa_cam,
        hsa_grp: payload.hsa_grp,
        hsa_ad: payload.hsa_ad,
        hsa_src: payload.hsa_src,
        hsa_tgt: payload.hsa_tgt,
        hsa_kw: payload.hsa_kw,
        hsa_mt: payload.hsa_mt,
        hsa_net: payload.hsa_net,
        hsa_ver: payload.hsa_ver,
      };

  if (options.emitDataLayer !== false) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...merged });
  }

  if (options.emitGtag !== false && typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, merged);
  }

  if (options.sendLocal !== false) {
    sendLocalTelemetry({
      ...merged,
      payload,
      attribution: inferredAttribution,
    });
  }

  setLastPage(pageWithQuery);
}
